import { Input, InputNumber, Select } from "antd";
import cuid from "cuid";
import { get, isNil, keyBy } from "lodash";
import PropTypes from "prop-types";
import { useRef } from "react";
import CanvasIcon from "./CanvasIcon";
import {
  applyConditionOperation,
  CONDITION_OPERATIONS,
  comparesValue,
  DEFAULT_CONDITION_OPERATION,
  fieldOperandOf,
  operationByValue,
} from "./catalog";
import ConditionValuePicker from "./condition-value-picker";
import usePanelMaxHeight from "./use-panel-max-height";
import { WORKSHOP_ICONS } from "./workshop-view";

const withValue = (condition, value) => ({
  ...condition,
  rightOperand: { ...(condition.rightOperand || {}), value },
});

const searchable = (input, option) =>
  String(get(option, "label") || "")
    .toLowerCase()
    .includes(input.toLowerCase());

const ValueEditor = ({
  condition,
  variable,
  fieldOptions,
  labels,
  conditionLabels,
  onChange,
}) => {
  const { valueType } = operationByValue(condition.operation) || {};
  if (valueType === "none") return null;

  if (comparesValue(operationByValue(condition.operation))) {
    return (
      <ConditionValuePicker
        className="workshop-select workshop-select--compact workshop-condition__value"
        aria-label={labels.VALUE}
        placeholder={labels.VALUE}
        condition={condition}
        fields={fieldOptions(fieldOperandOf(condition))}
        valueOptions={get(variable, "options.length") ? variable.options : null}
        valueType={valueType}
        labels={conditionLabels}
        onChange={(rightOperand) => onChange({ ...condition, rightOperand })}
      />
    );
  }

  if (valueType !== "boolean") return null;
  const value = get(condition, "rightOperand.value");
  return (
    <Select
      className="workshop-select workshop-select--compact workshop-condition__value"
      aria-label={labels.VALUE}
      placeholder={labels.VALUE}
      options={[
        { value: true, label: conditionLabels.YES },
        { value: false, label: conditionLabels.NO },
      ]}
      value={isNil(value) ? undefined : value}
      onChange={(next) => onChange(withValue(condition, next))}
    />
  );
};

ValueEditor.propTypes = {
  condition: PropTypes.object.isRequired,
  variable: PropTypes.object,
  fieldOptions: PropTypes.func.isRequired,
  labels: PropTypes.object.isRequired,
  conditionLabels: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

ValueEditor.defaultProps = {
  variable: null,
};

const WorkshopStateSettings = ({
  labels,
  conditionLabels,
  stateName,
  settings,
  variables,
  saving,
  disabled,
  onChange,
  onSave,
  onDelete,
  onClose,
}) => {
  const panelRef = useRef(null);
  usePanelMaxHeight(panelRef);
  const conditions = settings.conditions || [];
  const variableByAccessor = keyBy(variables, "accessor");
  const fieldOptions = (accessor) => {
    const options = variables.map((variable) => ({
      value: variable.accessor,
      label: variable.label || variable.accessor,
    }));
    if (!accessor || variableByAccessor[accessor]) return options;
    return [{ value: accessor, label: accessor }, ...options];
  };
  const operationOptions = CONDITION_OPERATIONS.map((operation) => ({
    value: operation.value,
    label:
      get(conditionLabels, ["OPERATIONS", operation.labelKey]) ||
      operation.value,
  }));
  const setConditions = (next) => onChange({ ...settings, conditions: next });
  const updateAt = (index, next) =>
    setConditions(
      conditions.map((condition, position) =>
        position === index ? next : condition,
      ),
    );

  return (
    <aside ref={panelRef} className="workshop-panel" aria-label={labels.TITLE}>
      <div className="workshop-panel__head">
        <div className="workshop-panel__head-text">
          <span className="workshop-panel__title">{labels.TITLE}</span>
          <span className="workshop-panel__subtitle">{stateName}</span>
        </div>
        <button
          type="button"
          className="workshop-panel__close"
          aria-label={labels.CLOSE}
          onClick={onClose}
        >
          <CanvasIcon path={WORKSHOP_ICONS.close} strokeWidth={2} />
        </button>
      </div>

      <div className="workshop-panel__body workshop-settings">
        <label className="workshop-settings__field">
          <span className="workshop-settings__label">{labels.NAME}</span>
          <Input
            className="workshop-input"
            value={settings.name}
            onChange={(event) =>
              onChange({ ...settings, name: event.target.value })
            }
          />
        </label>

        <div className="workshop-settings__conditions">
          <span className="workshop-settings__label">{labels.WHEN}</span>
          {conditions.map((condition, index) => {
            const accessor = get(condition, "leftOperand.accessor");
            return (
              <div key={condition.id || index} className="workshop-condition">
                <div className="workshop-condition__row">
                  <Select
                    showSearch
                    className="workshop-select workshop-select--compact workshop-condition__field"
                    aria-label={labels.FIELD}
                    placeholder={labels.FIELD}
                    options={fieldOptions(accessor)}
                    filterOption={searchable}
                    value={accessor || undefined}
                    onChange={(next) =>
                      updateAt(index, {
                        ...condition,
                        leftOperand: {
                          ...(condition.leftOperand || {}),
                          accessor: next,
                        },
                      })
                    }
                  />
                  <button
                    type="button"
                    className="workshop-condition__remove"
                    aria-label={labels.REMOVE_CONDITION}
                    onClick={() =>
                      setConditions(
                        conditions.filter((_, position) => position !== index),
                      )
                    }
                  >
                    <CanvasIcon path={WORKSHOP_ICONS.trash} size={16} />
                  </button>
                </div>
                <div className="workshop-condition__row">
                  <Select
                    className="workshop-select workshop-select--compact workshop-condition__operation"
                    aria-label={labels.OPERATION}
                    placeholder={labels.OPERATION}
                    options={operationOptions}
                    popupMatchSelectWidth={false}
                    value={condition.operation || DEFAULT_CONDITION_OPERATION}
                    onChange={(next) =>
                      updateAt(index, applyConditionOperation(condition, next))
                    }
                  />
                  <ValueEditor
                    condition={condition}
                    variable={variableByAccessor[accessor] || null}
                    fieldOptions={fieldOptions}
                    labels={labels}
                    conditionLabels={conditionLabels}
                    onChange={(next) => updateAt(index, next)}
                  />
                </div>
              </div>
            );
          })}
          {!conditions.length ? (
            <div className="workshop-settings__warning">
              <CanvasIcon
                path={WORKSHOP_ICONS.warn}
                size={16}
                strokeWidth={2}
              />
              <span>{labels.NO_CONDITION}</span>
            </div>
          ) : null}
          <button
            type="button"
            className="workshop-settings__add"
            onClick={() =>
              setConditions([
                ...conditions,
                {
                  id: cuid(),
                  leftOperand: {},
                  rightOperand: {},
                  operation: DEFAULT_CONDITION_OPERATION,
                },
              ])
            }
          >
            <CanvasIcon
              path={WORKSHOP_ICONS.plus}
              size={14}
              strokeWidth={2.4}
            />
            {labels.ADD_CONDITION}
          </button>
        </div>

        <label className="workshop-settings__field">
          <span className="workshop-settings__label">{labels.ORDER}</span>
          <InputNumber
            min={0}
            className="workshop-input workshop-settings__order"
            value={settings.order}
            onChange={(value) =>
              onChange({ ...settings, order: isNil(value) ? 0 : value })
            }
          />
          <span className="workshop-settings__hint">{labels.ORDER_HINT}</span>
        </label>
      </div>

      <div className="workshop-panel__foot">
        <button
          type="button"
          className="workshop-button workshop-button--primary"
          disabled={saving || disabled}
          onClick={onSave}
        >
          {labels.SAVE}
        </button>
        <span className="workshop-panel__spacer" />
        <button
          type="button"
          className="workshop-settings__delete"
          disabled={disabled}
          onClick={onDelete}
        >
          <CanvasIcon path={WORKSHOP_ICONS.trash} size={16} />
          {labels.DELETE}
        </button>
      </div>
    </aside>
  );
};

WorkshopStateSettings.propTypes = {
  labels: PropTypes.object.isRequired,
  conditionLabels: PropTypes.object,
  stateName: PropTypes.string,
  settings: PropTypes.shape({
    name: PropTypes.string,
    order: PropTypes.number,
    conditions: PropTypes.array,
  }).isRequired,
  variables: PropTypes.array,
  saving: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

WorkshopStateSettings.defaultProps = {
  conditionLabels: {},
  stateName: "",
  variables: [],
  saving: false,
  disabled: false,
};

export default WorkshopStateSettings;
